import React, { useState, useEffect, useMemo } from 'react';
import { API_BASE_URL } from './config';
import DataTable from './components/DataTable/DataTable';
import { EMAIL_LOGS_COLUMNS, VALIDATION_QUEUE_COLUMNS } from './constants/tableColumns';
import './AIMonitoring.css';

function AIMonitoring() {
    const [emailLogs, setEmailLogs] = useState([]);
    const [validationQueue, setValidationQueue] = useState([]);
    const [aiStats, setAiStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('overview');
    const [testRunning, setTestRunning] = useState(false);
    const [testResults, setTestResults] = useState(null);
    const [reprocessing, setReprocessing] = useState(false);
    const [reprocessResults, setReprocessResults] = useState(null);

    useEffect(() => {
        fetchData();
        // Refresh every 30 seconds
        const interval = setInterval(fetchData, 30000);
        return () => clearInterval(interval);
    }, []);

    // Parse validation queue data for DataTable
    const parsedValidationQueue = useMemo(() => {
        return validationQueue.map(item => {
            const jobData = JSON.parse(item.job_data || '{}');
            const issues = JSON.parse(item.validation_issues || '[]');
            
            return {
                id: item.id,
                organization: item.city_name,
                title: jobData.title,
                due_date: jobData.due_date,
                rfp_number: jobData.rfp_number,
                issues: issues,
                confidence: item.ai_confidence / 100, // Convert to decimal for consistency
                source: item.source,
                created_at: item.created_at,
            };
        });
    }, [validationQueue]);

    const fetchData = async () => {
        try {
            const [emailResponse, validationResponse, statsResponse] = await Promise.all([
                fetch(`${API_BASE_URL}/ai-monitoring/email-logs`, { credentials: 'include' }),
                fetch(`${API_BASE_URL}/ai-monitoring/validation-queue`, { credentials: 'include' }),
                fetch(`${API_BASE_URL}/ai-monitoring/stats`, { credentials: 'include' })
            ]);

            if (emailResponse.ok) {
                const data = await emailResponse.json();
                setEmailLogs(data.logs || []);
            }

            if (validationResponse.ok) {
                const data = await validationResponse.json();
                setValidationQueue(data.queue || []);
            }

            if (statsResponse.ok) {
                const data = await statsResponse.json();
                setAiStats(data);
            }
        } catch (error) {
            console.error('Error fetching AI monitoring data:', error);
        } finally {
            setLoading(false);
        }
    };

    const approveValidationItem = async (itemId) => {
        if (!window.confirm('Approve this job and add to database?')) return;

        try {
            const response = await fetch(`${API_BASE_URL}/ai-monitoring/validation-queue/${itemId}/approve`, {
                method: 'POST',
                credentials: 'include'
            });

            if (response.ok) {
                alert('Job approved and added to database!');
                fetchData();
            } else {
                alert('Failed to approve job');
            }
        } catch (error) {
            console.error('Error approving validation item:', error);
            alert('Error approving job');
        }
    };

    const rejectValidationItem = async (itemId) => {
        if (!window.confirm('Reject this job? It will be marked as reviewed.')) return;

        try {
            const response = await fetch(`${API_BASE_URL}/ai-monitoring/validation-queue/${itemId}/reject`, {
                method: 'POST',
                credentials: 'include'
            });

            if (response.ok) {
                alert('Job rejected');
                fetchData();
            } else {
                alert('Failed to reject job');
            }
        } catch (error) {
            console.error('Error rejecting validation item:', error);
            alert('Error rejecting job');
        }
    };

    const runAIAccuracyTest = async () => {
        if (!window.confirm('Run AI accuracy test? This will analyze how well the AI resolves conflicts compared to human decisions.')) return;

        setTestRunning(true);
        setTestResults(null);

        try {
            const response = await fetch(`${API_BASE_URL}/ai-monitoring/run-accuracy-test`, {
                method: 'POST',
                credentials: 'include'
            });

            if (response.ok) {
                const data = await response.json();
                setTestResults(data);
            } else {
                alert('Failed to run accuracy test');
            }
        } catch (error) {
            console.error('Error running accuracy test:', error);
            alert('Error running accuracy test');
        } finally {
            setTestRunning(false);
        }
    };

    const reprocessFailedEmails = async () => {
        const failedCount = emailLogs.filter(e => !e.success).length;
        
        if (failedCount === 0) {
            alert('No failed emails to reprocess!');
            return;
        }

        if (!window.confirm(`Reprocess ${failedCount} failed emails with the improved AI parser?\n\nThis will attempt to extract organization and title from emails that previously failed.`)) return;

        setReprocessing(true);
        setReprocessResults(null);

        try {
            const response = await fetch(`${API_BASE_URL}/ai-monitoring/reprocess-failed`, {
                method: 'POST',
                credentials: 'include'
            });

            if (response.ok) {
                const data = await response.json();
                setReprocessResults(data);
                
                // Refresh email logs to show updated results
                fetchData();
                
                if (data.success) {
                    alert(`✅ ${data.message}`);
                } else {
                    alert(`❌ Error: ${data.error}`);
                }
            } else {
                alert('Failed to reprocess emails');
            }
        } catch (error) {
            console.error('Error reprocessing emails:', error);
            alert('Error reprocessing emails');
        } finally {
            setReprocessing(false);
        }
    };

    if (loading) {
        return (
            <div className="container mt-4">
                <h2>Loading AI Monitoring...</h2>
                <div className="spinner-border" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
            </div>
        );
    }

    return (
        <div className="container-fluid mt-4 ai-monitoring-page">
            <div className="page-header">
                <h2>🤖 AI System Monitoring</h2>
                <p className="text-muted">
                    Monitor email watcher, data validation, and AI performance
                </p>
            </div>

            {/* Tabs */}
            <ul className="nav nav-tabs mb-4">
                <li className="nav-item">
                    <button 
                        className={`nav-link ${activeTab === 'overview' ? 'active' : ''}`}
                        onClick={() => setActiveTab('overview')}
                    >
                        📊 Overview
                    </button>
                </li>
                <li className="nav-item">
                    <button 
                        className={`nav-link ${activeTab === 'email' ? 'active' : ''}`}
                        onClick={() => setActiveTab('email')}
                    >
                        📧 Email Logs ({emailLogs.filter(e => !e.success).length} errors)
                    </button>
                </li>
                <li className="nav-item">
                    <button 
                        className={`nav-link ${activeTab === 'validation' ? 'active' : ''}`}
                        onClick={() => setActiveTab('validation')}
                    >
                        ⚠️ Validation Queue ({validationQueue.length} pending)
                    </button>
                </li>
            </ul>

            {/* Overview Tab */}
            {activeTab === 'overview' && aiStats && (
                <div className="overview-tab">
                    <div className="row">
                        {/* Email Stats */}
                        <div className="col-md-4">
                            <div className="stat-card">
                                <h5>📧 Email Watcher</h5>
                                <div className="stat-value">{aiStats.email_processed_today || 0}</div>
                                <div className="stat-label">Emails Today</div>
                                <div className="stat-detail">
                                    Success: {aiStats.email_success_rate || 0}% 
                                    {aiStats.email_errors_today > 0 && (
                                        <span className="text-danger"> ({aiStats.email_errors_today} errors)</span>
                                    )}
                                </div>
                                <div className="stat-detail">Cost today: ${(aiStats.email_cost_today || 0).toFixed(4)}</div>
                            </div>
                        </div>

                        {/* Validation Stats */}
                        <div className="col-md-4">
                            <div className="stat-card">
                                <h5>✅ Data Validation</h5>
                                <div className="stat-value">{aiStats.validation_pass_rate || 0}%</div>
                                <div className="stat-label">Pass Rate (Last 7 Days)</div>
                                <div className="stat-detail">Validated: {aiStats.jobs_validated_week || 0} jobs</div>
                                <div className="stat-detail">
                                    Failed: {aiStats.validation_failures || 0}
                                    {aiStats.validation_failures > 0 && (
                                        <span className="text-warning"> (need review)</span>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Conflict Stats */}
                        <div className="col-md-4">
                            <div className="stat-card">
                                <h5>🤝 Conflict Resolution</h5>
                                <div className="stat-value">{aiStats.ai_accuracy || 0}%</div>
                                <div className="stat-label">AI Accuracy (When Tested)</div>
                                <div className="stat-detail">Conflicts resolved: {aiStats.conflicts_resolved || 0}</div>
                                <div className="stat-detail">
                                    Auto-resolved: {aiStats.auto_resolved_count || 0}
                                    {aiStats.auto_resolved_count > 0 && (
                                        <span className="text-success"> (Learning mode OFF)</span>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Cost Summary */}
                    <div className="row mt-4">
                        <div className="col-md-12">
                            <div className="stat-card cost-summary">
                                <h5>💰 AI Costs</h5>
                                <div className="row">
                                    <div className="col-md-3">
                                        <div className="cost-metric">
                                            <div className="cost-value">${(aiStats.cost_today || 0).toFixed(4)}</div>
                                            <div className="cost-label">Today</div>
                                        </div>
                                    </div>
                                    <div className="col-md-3">
                                        <div className="cost-metric">
                                            <div className="cost-value">${(aiStats.cost_week || 0).toFixed(2)}</div>
                                            <div className="cost-label">This Week</div>
                                        </div>
                                    </div>
                                    <div className="col-md-3">
                                        <div className="cost-metric">
                                            <div className="cost-value">${(aiStats.cost_month || 0).toFixed(2)}</div>
                                            <div className="cost-label">This Month</div>
                                        </div>
                                    </div>
                                    <div className="col-md-3">
                                        <div className="cost-metric">
                                            <div className="cost-value est">${(aiStats.cost_projected || 0).toFixed(2)}</div>
                                            <div className="cost-label">Projected/Month</div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Admin Actions */}
                    <div className="row mt-4">
                        <div className="col-md-12">
                            <div className="stat-card">
                                <h5>🔧 Admin Actions</h5>
                                <div className="d-flex gap-2 flex-wrap">
                                    <button 
                                        className="btn btn-primary"
                                        onClick={runAIAccuracyTest}
                                        disabled={testRunning}
                                    >
                                        {testRunning ? '⏳ Testing...' : '🎯 Run AI Accuracy Test'}
                                    </button>
                                    <button 
                                        className="btn btn-secondary"
                                        onClick={fetchData}
                                    >
                                        🔄 Refresh Data
                                    </button>
                                </div>

                                {/* Test Results */}
                                {testResults && (
                                    <div className="mt-3 p-3 bg-light rounded">
                                        <h6>Test Results:</h6>
                                        {testResults.error ? (
                                            <div className="alert alert-danger mb-0">
                                                {testResults.error}
                                            </div>
                                        ) : (
                                            <div>
                                                <p className="mb-2">
                                                    <strong>Overall Accuracy:</strong> 
                                                    <span className={`ms-2 badge ${testResults.accuracy >= 90 ? 'bg-success' : testResults.accuracy >= 80 ? 'bg-warning' : 'bg-danger'}`}>
                                                        {testResults.accuracy}%
                                                    </span>
                                                </p>
                                                <p className="mb-2">
                                                    <strong>Total Tested:</strong> {testResults.total_tested} conflicts
                                                </p>
                                                <p className="mb-2">
                                                    <strong>Correct:</strong> {testResults.correct} | 
                                                    <strong className="ms-2">Incorrect:</strong> {testResults.incorrect}
                                                </p>
                                                {testResults.details && (
                                                    <details className="mt-2">
                                                        <summary className="cursor-pointer">Show Details</summary>
                                                        <pre className="mt-2 p-2 bg-white rounded" style={{fontSize: '0.75rem'}}>
                                                            {JSON.stringify(testResults.details, null, 2)}
                                                        </pre>
                                                    </details>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Email Logs Tab - Sprint 20: Refactored with DataTable */}
            {activeTab === 'email' && (
                <div className="email-logs-tab">
                    <div className="d-flex justify-content-between align-items-center mb-3">
                        <h4 className="mb-0">Email Processing Logs (Last 50)</h4>
                        <button 
                            className="btn btn-primary"
                            onClick={reprocessFailedEmails}
                            disabled={reprocessing || emailLogs.filter(e => !e.success).length === 0}
                        >
                            {reprocessing ? (
                                <>
                                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                    Reprocessing...
                                </>
                            ) : (
                                <>
                                    🔄 Reprocess Failed Emails ({emailLogs.filter(e => !e.success).length})
                                </>
                            )}
                        </button>
                    </div>
                    
                    {reprocessResults && reprocessResults.success && (
                        <div className="alert alert-info alert-dismissible fade show" role="alert">
                            <strong>Reprocessing Complete!</strong>
                            <ul className="mb-0 mt-2">
                                <li>Processed: {reprocessResults.processed} emails</li>
                                <li>✅ Succeeded: {reprocessResults.succeeded}</li>
                                <li>❌ Failed: {reprocessResults.failed}</li>
                            </ul>
                            <button type="button" className="btn-close" onClick={() => setReprocessResults(null)} aria-label="Close"></button>
                        </div>
                    )}
                    
                    <DataTable
                        columns={EMAIL_LOGS_COLUMNS}
                        data={emailLogs}
                        tableId="email-logs-table"
                        enableRowSelection={false}
                        enableColumnResizing={true}
                        enableColumnOrdering={true}
                        enableFilters={false}
                        enablePagination={false}
                        className="email-logs-table"
                        defaultSorting={[{ id: 'processed_at', desc: true }]}
                    />
                </div>
            )}

            {/* Validation Queue Tab - Sprint 20: Refactored with DataTable */}
            {activeTab === 'validation' && (
                <div className="validation-queue-tab">
                    <h4>Validation Queue - Jobs That Failed Quality Check</h4>
                    <p className="text-muted">
                        These jobs had low AI confidence (&lt;75%) and need your review before being added to the database.
                    </p>

                    {validationQueue.length === 0 ? (
                        <div className="alert alert-success">
                            ✅ No jobs in validation queue - all data passed quality checks!
                        </div>
                    ) : (
                        <DataTable
                            columns={[
                                ...VALIDATION_QUEUE_COLUMNS,
                                {
                                    id: 'actions',
                                    header: 'Actions',
                                    cell: ({ row }) => (
                                        <div className="d-flex gap-1 flex-nowrap">
                                            <button 
                                                className="btn btn-sm btn-success"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    approveValidationItem(row.original.id);
                                                }}
                                                title="Job looks good - add to database"
                                            >
                                                ✅ Approve
                                            </button>
                                            <button 
                                                className="btn btn-sm btn-danger"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    rejectValidationItem(row.original.id);
                                                }}
                                                title="Job is bad - reject permanently"
                                            >
                                                ❌ Reject
                                            </button>
                                        </div>
                                    ),
                                    enableSorting: false,
                                    enableHiding: false,
                                    size: 180,
                                },
                            ]}
                            data={parsedValidationQueue}
                            tableId="validation-queue-table"
                            enableRowSelection={false}
                            enableColumnResizing={true}
                            enableColumnOrdering={true}
                            enableFilters={false}
                            enablePagination={false}
                            className="validation-queue-table"
                            defaultSorting={[{ id: 'created_at', desc: true }]}
                        />
                    )}
                </div>
            )}
        </div>
    );
}

export default AIMonitoring;

